import { configureApiClient, Role } from "@cryptors/api-client";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BookIcon from "@mui/icons-material/Book";
import CampaignIcon from "@mui/icons-material/Campaign";
import FaceIcon from "@mui/icons-material/Face";
import HelpIcon from "@mui/icons-material/Help";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import PersonIcon from "@mui/icons-material/Person";
import TranslateIcon from "@mui/icons-material/Translate";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import {
  Authenticated,
  BaseRecord,
  CanAccess,
  DataProvider,
  GetManyParams,
  GetManyResponse,
  Refine,
} from "@refinedev/core";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import {
  AuthPage,
  ErrorComponent,
  RefineSnackbarProvider,
  ThemedLayout,
  useNotificationProvider,
} from "@refinedev/mui";
import dataProvider from "@refinedev/nestjsx-crud";
import routerBindings, {
  DocumentTitleHandler,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import axios from "axios";
import { fr } from "date-fns/locale/fr";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from "react-router-dom";

import { Header, Sider } from "./components";
import { Title } from "./components/title/Title";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { AdvertisementCreateEdit } from "./pages/advertisement/AdvertisementCreateEdit";
import { AdvertisementList } from "./pages/advertisement/AdvertisementList";
import { AdvertisementShow } from "./pages/advertisement/AdvertisementShow";
import { AiTraining } from "./pages/ai-training/AiTraining";
import { AnnouncementCreateEdit } from "./pages/announcement/AnnouncementCreateEdit";
import { AnnouncementList } from "./pages/announcement/AnnouncementList";
import { AnnouncementShow } from "./pages/announcement/AnnouncementShow";
import { AvatarCreate } from "./pages/avatar/AvatarCreate";
import { AvatarEdit } from "./pages/avatar/AvatarEdit";
import { AvatarList } from "./pages/avatar/AvatarList";
import { AvatarShow } from "./pages/avatar/AvatarShow";
import { ChatMessageCreate } from "./pages/chatMessage/ChatMessageCreate";
import { ChatMessageEdit } from "./pages/chatMessage/ChatMessageEdit";
import { ChatMessageList } from "./pages/chatMessage/ChatMessageList";
import { ChatMessageShow } from "./pages/chatMessage/ChatMessageShow";
import { CitiesCreate } from "./pages/cities/CitiesCreate";
import { CitiesEdit } from "./pages/cities/CitiesEdit";
import { CitiesList } from "./pages/cities/CitiesList";
import { CitiesShow } from "./pages/cities/CitiesShow";
import CryptorAdvertisementsEdit from "./pages/cryptor/CryptorAdvertisementsEdit";
import { CryptorCreate } from "./pages/cryptor/CryptorCreate";
import { CryptorEdit } from "./pages/cryptor/CryptorEdit";
import { CryptorList } from "./pages/cryptor/CryptorList";
import { CryptorShow } from "./pages/cryptor/CryptorShow";
import { Dashboard } from "./pages/dashboard/Dashboard";
import { FaqCreate } from "./pages/faq/FaqCreate";
import { FaqEdit } from "./pages/faq/FaqEdit";
import { FaqList } from "./pages/faq/FaqList";
import { FaqShow } from "./pages/faq/FaqShow";
import HintsEdit from "./pages/hints/HintsEdit";
import HintsShow from "./pages/hints/HintsShow";
import { LevelCreateEdit } from "./pages/level/LevelCreateEdit";
import { LevelList } from "./pages/level/LevelList";
import { LevelShow } from "./pages/level/LevelShow";
import QuestionsEdit from "./pages/questions/QuestionsEdit";
import QuestionsShow from "./pages/questions/QuestionsShow";
import { QuizEdit } from "./pages/quiz/QuizEdit";
import { TranslationList } from "./pages/translation/TranslationList";
import { UserCreate } from "./pages/user/UserCreate";
import { UserEdit } from "./pages/user/UserEdit";
import { UserList } from "./pages/user/UserList";
import { UserShow } from "./pages/user/UserShow";
import accessControlProvider from "./providers/accessControlProvider";
import authProvider, { getCurrentRole } from "./providers/authProvider";
import queryClient from "./queryClient";

const apiUrl = `${import.meta.env.VITE_BACKEND_URL as string}/admin`;

const baseDataProvider = dataProvider(apiUrl, axios);
const unwrapGetMany = async <TData extends BaseRecord = BaseRecord>(
  params: GetManyParams,
): Promise<GetManyResponse<TData>> => {
  const res = await baseDataProvider.getMany!<TData>(params);
  const body = res.data as unknown;
  const list = Array.isArray(body)
    ? body
    : Array.isArray((body as { data?: unknown[] })?.data)
      ? (body as { data: unknown[] }).data
      : [];
  return { data: list as TData[] };
};
const appDataProvider: DataProvider = {
  ...baseDataProvider,
  getMany: unwrapGetMany,
};

const AccessDeniedPage = () =>
  getCurrentRole() === Role.CLIENT ? (
    <Navigate to="/dashboard" replace />
  ) : (
    <ErrorComponent />
  );

const protectedPage = (
  resource: string,
  action: string,
  page: ReactElement,
) => (
  <CanAccess
    resource={resource}
    action={action}
    fallback={<AccessDeniedPage />}
  >
    {page}
  </CanAccess>
);

const App = () => {
  axios.defaults.baseURL = import.meta.env.VITE_BACKEND_URL;
  axios.defaults.headers.common.language = "fr";
  configureApiClient(axios);

  const { t, i18n } = useTranslation();
  const i18nProvider = {
    translate: (key: string, params?: Record<string, unknown>) =>
      String(t(key, params)),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <BrowserRouter>
        <RefineKbarProvider>
          <ColorModeContextProvider>
            <CssBaseline />
            <GlobalStyles styles={{ html: { WebkitFontSmoothing: "auto" } }} />
            <RefineSnackbarProvider>
              <DevtoolsProvider>
                <Refine
                  notificationProvider={useNotificationProvider}
                  routerProvider={routerBindings}
                  authProvider={authProvider}
                  accessControlProvider={accessControlProvider}
                  i18nProvider={i18nProvider}
                  dataProvider={appDataProvider}
                  options={{
                    syncWithLocation: true,
                    warnWhenUnsavedChanges: true,
                    reactQuery: { clientConfig: queryClient },
                    projectId: "R3cdLL-jW6N3k-NlYSeq",
                  }}
                  resources={[
                    {
                      name: "dashboard",
                      list: "/dashboard",
                      meta: { hide: true },
                    },
                    {
                      name: "users",
                      list: "/users",
                      create: "/users/create",
                      show: "/users/show/:id",
                      edit: "/users/edit/:id",
                      meta: { icon: <PersonIcon /> },
                    },
                    {
                      name: "cryptors",
                      list: "/cryptors",
                      create: "/cryptors/create",
                      show: "/cryptors/show/:id",
                      edit: "/cryptors/edit/:id",
                      meta: { icon: <BookIcon /> },
                    },
                    {
                      name: "cities",
                      list: "/cities",
                      create: "/cities/create",
                      show: "/cities/show/:id",
                      edit: "/cities/edit/:id",
                      meta: { icon: <LocationCityIcon /> },
                    },
                    {
                      name: "faq",
                      list: "/faq",
                      create: "/faq/create",
                      show: "/faq/show/:id",
                      edit: "/faq/edit/:id",
                      meta: { icon: <HelpIcon /> },
                    },
                    {
                      name: "avatars",
                      list: "/avatars",
                      create: "/avatars/create",
                      show: "/avatars/show/:id",
                      edit: "/avatars/edit/:id",
                      meta: { icon: <FaceIcon /> },
                    },
                    {
                      name: "hints",
                      show: "/hints/show",
                      edit: "/hints/edit",
                      meta: { hide: true },
                    },
                    {
                      name: "questions",
                      show: "/questions/show",
                      edit: "/questions/edit",
                      meta: { hide: true },
                    },
                    {
                      name: "quiz",
                      edit: "/cryptors/:cryptorId/quiz",
                      meta: { hide: true },
                    },
                    {
                      name: "chat-message",
                      list: "/cryptors/:cryptorId/chat-message",
                      create: "/cryptors/:cryptorId/chat-message/create",
                      show: "/cryptors/:cryptorId/chat-message/:id",
                      edit: "/cryptors/:cryptorId/chat-message/edit/:id",
                      meta: { hide: true },
                    },
                    {
                      name: "levels",
                      list: "/levels",
                      create: "/levels/create",
                      show: "/levels/show/:id",
                      edit: "/levels/edit/:id",
                      meta: { icon: <LibraryBooksIcon /> },
                    },
                    {
                      name: "advertisements",
                      list: "/advertisements",
                      create: "/advertisements/create",
                      show: "/advertisements/show/:id",
                      edit: "/advertisements/edit/:id",
                      meta: { icon: <AdsClickIcon /> },
                    },
                    {
                      name: "announcements",
                      list: "/announcements",
                      create: "/announcements/create",
                      show: "/announcements/show/:id",
                      edit: "/announcements/edit/:id",
                      meta: { icon: <CampaignIcon />, label: "Annonces" },
                    },
                    {
                      name: "translation",
                      list: "/translation",
                      meta: { icon: <TranslateIcon /> },
                    },
                    {
                      name: "ai-training",
                      list: "/ai-training",
                      meta: { icon: <AutoAwesomeIcon /> },
                    },
                  ]}
                >
                  <Routes>
                    <Route
                      element={
                        <Authenticated
                          key="login"
                          redirectOnFail="/login"
                          appendCurrentPathToQuery={false}
                        >
                          <ThemedLayout
                            Header={Header}
                            Sider={Sider}
                            Title={Title}
                          >
                            <Outlet />
                          </ThemedLayout>
                        </Authenticated>
                      }
                    >
                      <Route
                        path="/"
                        element={<Navigate to="/dashboard" replace />}
                      />

                      <Route
                        path="/dashboard"
                        element={protectedPage(
                          "dashboard",
                          "list",
                          <Dashboard />,
                        )}
                      />

                      <Route
                        path="/announcements"
                        element={protectedPage(
                          "announcements",
                          "list",
                          <AnnouncementList />,
                        )}
                      />
                      <Route
                        path="/announcements/create"
                        element={protectedPage(
                          "announcements",
                          "create",
                          <AnnouncementCreateEdit />,
                        )}
                      />
                      <Route
                        path="/announcements/show/:id"
                        element={protectedPage(
                          "announcements",
                          "show",
                          <AnnouncementShow />,
                        )}
                      />
                      <Route
                        path="/announcements/edit/:id"
                        element={protectedPage(
                          "announcements",
                          "edit",
                          <AnnouncementCreateEdit isEditing />,
                        )}
                      />

                      <Route
                        path="/users"
                        element={protectedPage("users", "list", <UserList />)}
                      />
                      <Route
                        path="/users/edit/:id"
                        element={protectedPage("users", "edit", <UserEdit />)}
                      />
                      <Route
                        path="/users/show/:id"
                        element={protectedPage("users", "show", <UserShow />)}
                      />
                      <Route
                        path="/users/create"
                        element={protectedPage(
                          "users",
                          "create",
                          <UserCreate />,
                        )}
                      />

                      <Route
                        path="/cryptors"
                        element={protectedPage(
                          "cryptors",
                          "list",
                          <CryptorList />,
                        )}
                      />
                      <Route
                        path="/cryptors/edit/:id"
                        element={protectedPage(
                          "cryptors",
                          "edit",
                          <CryptorEdit />,
                        )}
                      />
                      <Route
                        path="/cryptors/show/:id"
                        element={protectedPage(
                          "cryptors",
                          "show",
                          <CryptorShow />,
                        )}
                      />
                      <Route
                        path="/cryptors/create"
                        element={protectedPage(
                          "cryptors",
                          "create",
                          <CryptorCreate />,
                        )}
                      />

                      <Route
                        path="/hints/edit"
                        element={protectedPage("hints", "edit", <HintsEdit />)}
                      />
                      <Route
                        path="/hints/show"
                        element={protectedPage("hints", "show", <HintsShow />)}
                      />

                      <Route
                        path="/questions/edit"
                        element={protectedPage(
                          "questions",
                          "edit",
                          <QuestionsEdit />,
                        )}
                      />
                      <Route
                        path="/questions/show"
                        element={protectedPage(
                          "questions",
                          "show",
                          <QuestionsShow />,
                        )}
                      />

                      <Route
                        path="/cryptors/advertisements/edit"
                        element={protectedPage(
                          "cryptors",
                          "edit",
                          <CryptorAdvertisementsEdit />,
                        )}
                      />

                      <Route
                        path="/cryptors/:cryptorId/quiz"
                        element={protectedPage("quiz", "edit", <QuizEdit />)}
                      />

                      <Route
                        path="/faq"
                        element={protectedPage("faq", "list", <FaqList />)}
                      />
                      <Route
                        path="/faq/create"
                        element={protectedPage("faq", "create", <FaqCreate />)}
                      />
                      <Route
                        path="/faq/show/:id"
                        element={protectedPage("faq", "show", <FaqShow />)}
                      />
                      <Route
                        path="/faq/edit/:id"
                        element={protectedPage("faq", "edit", <FaqEdit />)}
                      />

                      <Route
                        path="/cities"
                        element={protectedPage(
                          "cities",
                          "list",
                          <CitiesList />,
                        )}
                      />
                      <Route
                        path="/cities/create"
                        element={protectedPage(
                          "cities",
                          "create",
                          <CitiesCreate />,
                        )}
                      />
                      <Route
                        path="/cities/show/:id"
                        element={protectedPage(
                          "cities",
                          "show",
                          <CitiesShow />,
                        )}
                      />
                      <Route
                        path="/cities/edit/:id"
                        element={protectedPage(
                          "cities",
                          "edit",
                          <CitiesEdit />,
                        )}
                      />

                      <Route
                        path="/avatars"
                        element={protectedPage(
                          "avatars",
                          "list",
                          <AvatarList />,
                        )}
                      />
                      <Route
                        path="/avatars/create"
                        element={protectedPage(
                          "avatars",
                          "create",
                          <AvatarCreate />,
                        )}
                      />
                      <Route
                        path="/avatars/show/:id"
                        element={protectedPage(
                          "avatars",
                          "show",
                          <AvatarShow />,
                        )}
                      />
                      <Route
                        path="/avatars/edit/:id"
                        element={protectedPage(
                          "avatars",
                          "edit",
                          <AvatarEdit />,
                        )}
                      />

                      <Route
                        path="/levels"
                        element={protectedPage("levels", "list", <LevelList />)}
                      />
                      <Route
                        path="/levels/show/:id"
                        element={protectedPage("levels", "show", <LevelShow />)}
                      />
                      <Route
                        path="/levels/create"
                        element={protectedPage(
                          "levels",
                          "create",
                          <LevelCreateEdit />,
                        )}
                      />
                      <Route
                        path="/levels/edit/:id"
                        element={protectedPage(
                          "levels",
                          "edit",
                          <LevelCreateEdit isEditing />,
                        )}
                      />

                      <Route
                        path="/advertisements"
                        element={protectedPage(
                          "advertisements",
                          "list",
                          <AdvertisementList />,
                        )}
                      />
                      <Route
                        path="/advertisements/show/:id"
                        element={protectedPage(
                          "advertisements",
                          "show",
                          <AdvertisementShow />,
                        )}
                      />
                      <Route
                        path="/advertisements/create"
                        element={protectedPage(
                          "advertisements",
                          "create",
                          <AdvertisementCreateEdit />,
                        )}
                      />
                      <Route
                        path="/advertisements/edit/:id"
                        element={protectedPage(
                          "advertisements",
                          "edit",
                          <AdvertisementCreateEdit />,
                        )}
                      />

                      <Route
                        path="/cryptors/:cryptorId/chat-message"
                        element={protectedPage(
                          "chat-message",
                          "list",
                          <ChatMessageList />,
                        )}
                      />
                      <Route
                        path="/cryptors/:cryptorId/chat-message/create"
                        element={protectedPage(
                          "chat-message",
                          "create",
                          <ChatMessageCreate />,
                        )}
                      />
                      <Route
                        path="/cryptors/:cryptorId/chat-message/edit/:id"
                        element={protectedPage(
                          "chat-message",
                          "edit",
                          <ChatMessageEdit />,
                        )}
                      />
                      <Route
                        path="/cryptors/:cryptorId/chat-message/:id"
                        element={protectedPage(
                          "chat-message",
                          "show",
                          <ChatMessageShow />,
                        )}
                      />

                      <Route
                        path="/translation"
                        element={protectedPage(
                          "translation",
                          "list",
                          <TranslationList />,
                        )}
                      />

                      <Route
                        path="/ai-training"
                        element={protectedPage(
                          "ai-training",
                          "list",
                          <AiTraining />,
                        )}
                      />

                      <Route path="*" element={<ErrorComponent />} />
                    </Route>
                    <Route
                      path="login"
                      element={
                        <AuthPage
                          type="login"
                          title="Cryptors"
                          registerLink={false}
                          forgotPasswordLink={false}
                        />
                      }
                    />
                  </Routes>
                  <RefineKbar />
                  <UnsavedChangesNotifier />
                  <DocumentTitleHandler />
                </Refine>
                {import.meta.env.DEV && <DevtoolsPanel />}
              </DevtoolsProvider>
            </RefineSnackbarProvider>
          </ColorModeContextProvider>
        </RefineKbarProvider>
      </BrowserRouter>
    </LocalizationProvider>
  );
};

export default App;
